import React, { useEffect, useState } from 'react';
import useAuth from '../auth/feature/auth';
import { useNavigate } from 'react-router-dom';
import { ROUTER_LINKS } from '../common/consts/app-keys.const';

interface ChangeSection {
  type: string;
  items: string[];
}

interface ChangeVersion {
  version: string;
  date: string;
  sections: ChangeSection[];
}

function parseChangelog(md: string): ChangeVersion[] {
  const versions: ChangeVersion[] = [];
  let currentVersion: ChangeVersion | null = null;
  let currentSection: ChangeSection | null = null;

  for (const raw of md.split('\n')) {
    const line = raw.trim();

    const versionMatch = line.match(/^##\s+\[?v?([\d.]+(?:-\w+)?)\]?(?:\s+-\s+(.+))?/);
    if (versionMatch) {
      if (currentSection && currentVersion) currentVersion.sections.push(currentSection);
      if (currentVersion) versions.push(currentVersion);
      currentSection = null;
      currentVersion = {
        version: versionMatch[1],
        date: versionMatch[2]?.trim() ?? '',
        sections: [],
      };
      continue;
    }

    const sectionMatch = line.match(/^###\s+(.+)/);
    if (sectionMatch && currentVersion) {
      if (currentSection) currentVersion.sections.push(currentSection);
      currentSection = { type: sectionMatch[1].trim(), items: [] };
      continue;
    }

    const bulletMatch = line.match(/^[-*]\s+(.+)/);
    if (bulletMatch) {
      const text = bulletMatch[1].replace(/\*\*(.+?)\*\*/g, '$1');
      if (currentSection) {
        currentSection.items.push(text);
      } else if (currentVersion) {
        let defaultSection = currentVersion.sections.find((s) => s.type === 'Changes');
        if (!defaultSection) {
          defaultSection = { type: 'Changes', items: [] };
          currentVersion.sections.push(defaultSection);
        }
        defaultSection.items.push(text);
      }
    }
  }

  if (currentSection && currentVersion) currentVersion.sections.push(currentSection);
  if (currentVersion) versions.push(currentVersion);

  return versions;
}

const SECTION_COLORS: Record<string, { bg: string; text: string }> = {
  Added:          { bg: '#dcfce7', text: '#15803d' },
  Fixed:          { bg: '#fef9c3', text: '#854d0e' },
  Changed:        { bg: '#dbeafe', text: '#1d4ed8' },
  Removed:        { bg: '#fee2e2', text: '#b91c1c' },
  Deprecated:     { bg: '#f3e8ff', text: '#7e22ce' },
  Security:       { bg: '#ffe4e6', text: '#be123c' },
  Infrastructure: { bg: '#f0f9ff', text: '#0369a1' },
  Changes:        { bg: '#f1f5f9', text: '#475569' },
};

function sectionStyle(type: string) {
  return SECTION_COLORS[type] ?? { bg: '#f1f5f9', text: '#475569' };
}

const SiteChangelog: React.FC = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [versions, setVersions] = useState<ChangeVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAdmin) navigate(ROUTER_LINKS.ROOT);
  }, [isAdmin, navigate]);

  useEffect(() => {
    fetch('/standby-changelog.md')
      .then((res) => {
        if (!res.ok) throw new Error(`${res.status}`);
        return res.text();
      })
      .then((text) => {
        setVersions(parseChangelog(text));
        setLoading(false);
      })
      .catch(() => {
        setError('Changelog could not be loaded.');
        setLoading(false);
      });
  }, []);

  if (!isAdmin) return null;

  if (loading) {
    return (
      <div className="w-full max-w-[860px] mx-auto py-[40px] px-[20px] text-gray-400">
        Loading changelog…
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-[860px] mx-auto py-[40px] px-[20px] text-red-500">
        {error}
      </div>
    );
  }

  return (
    <div className="w-full max-w-[860px] mx-auto py-[40px] px-[20px]">
      <div className="flex items-center gap-[12px] mb-[8px]">
        <button
          onClick={() => navigate(ROUTER_LINKS.ADMIN)}
          className="text-[14px] text-[#0ea5c9] hover:underline"
        >
          Site Admin
        </button>
        <span className="text-gray-400">/</span>
        <h1 className="text-[28px] font-display font-bold text-[#111827] tracking-tight">
          Changelog
        </h1>
      </div>
      <p className="text-[14px] text-gray-600 mb-[40px]">
        A history of updates and improvements to Standby.
      </p>

      <div className="flex flex-col gap-[32px]">
        {versions.map((v, i) => (
          <div key={i} className="border-l-2 border-gray-200 pl-[24px]">
            <div className="flex items-baseline gap-[12px] mb-[16px]">
              <span className="text-[18px] font-display font-bold text-[#111827]">v{v.version}</span>
              {v.date && <span className="text-[13px] text-gray-500">{v.date}</span>}
            </div>
            <div className="flex flex-col gap-[16px]">
              {v.sections.map((sec, j) => {
                const colors = sectionStyle(sec.type);
                return (
                  <div key={j}>
                    <span
                      className="inline-block rounded-[4px] px-[8px] py-[2px] mb-[10px] text-[11px] font-bold uppercase tracking-wider"
                      style={{ background: colors.bg, color: colors.text }}
                    >
                      {sec.type}
                    </span>
                    <ul className="m-0 pl-[18px] flex flex-col gap-[6px]">
                      {sec.items.map((item, k) => (
                        <li key={k} className="text-[14px] leading-[1.65] text-gray-700">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {versions.length === 0 && (
          <p className="text-[14px] text-gray-500">No changelog entries yet.</p>
        )}
      </div>
    </div>
  );
};

export default SiteChangelog;
